import React, {useCallback} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {PAGE_SIZE, useTable} from "../../hooks/table/tableHook";
import Table from "./Table";
import TablePagination from "./TablePagination";
import Button from "./Button";
import TableFilter from "./TableFilter";
import {useDispatch, useSelector} from "react-redux";
import Fragment from "./Fragment";
import useUpdateOnlyEffect from "../../hooks/effect/useUpdateOnlyEffect";
import {constructPage} from "../../utils/APIUtils";

const DataList = (props) => {
    const {
        columns,
        onClick,
        createButtonLabel,
        refreshState: refreshDependantState,
        cardTitle,
    } = props;
    const {t} = useTranslation();
    const navigate = useNavigate();

    const dispatch = useDispatch();
    const tableData = useSelector(props.selector);
    const loading = props.loadingSelector ? useSelector(props.loadingSelector) : false;
    const fetchData = useCallback((page, filter, sort) => {
        return dispatch(props.fetchAction({
            page: page,
            filter: filter,
            sort: sort
        }));
    }, [dispatch, props.fetchAction]);

    const useTableHandle = useTable({
        total: tableData?.totalElements || 0,
        data: tableData ? tableData : {content: []},
        page: constructPage(0, PAGE_SIZE),
        filter: props.defaultFilter,
        sort: props.defaultSort
    }, onRowClick, fetchData);
    const {
        refreshList,
        selection,
        onSelection,
        page,
        onPage,
        sort,
        onSort,
        onFilter,
        total,
        filter,
    } = useTableHandle;
    function onRowClick(entry) {
        if (props.disableOnClick) {
            return;
        }
        onClick ? onClick(entry) : navigate("edit/" + entry.id);
    }


    useUpdateOnlyEffect(() => {
        if (refreshDependantState) {
            refreshList();
        }
    }, [refreshDependantState]);

    return (
        <div>
            {!cardTitle && props.title && <h2>{props.title}</h2>}
            <div className="card table-card has-overflow-y-auto">
                {cardTitle && (
                    <header className="card-header">
                        <p className="card-header-title">
                            {cardTitle}
                        </p>
                    </header>
                )}
                <div className="card-content">
                    <Table
                        columns={columns}
                        data={tableData?.content ? tableData.content : []}
                        onSelection={onSelection}
                        selection={selection}
                        enableDeselection={false}
                        rowId={props.rowId}
                        onSort={!props.groupRowsBy ? onSort : null}
                        sort={sort}
                        groupRowsBy={props.groupRowsBy}
                        loading={loading ?? false}
                    />
                    <div className='mt-4'>
                        {tableData && tableData.totalElements > 0 && total > 0 &&
                            <TablePagination
                                page={page.page}
                                total={total}
                                pageSize={page.size}
                                onPage={onPage}
                            />
                        }
                    </div>
                </div>
            </div>
            {((props.filterColumns && props.filterColumns.length > 0) || props.showCreateButton) &&
                <div className="level card-footer-buttons">
                    <div className="level-left">

                    <Fragment rendered={props.showCreateButton}>
                            <Button label={createButtonLabel ?? t('create_new')} color="primary"
                                    onClick={() => {
                                        props.onCreateClick != null ? props.onCreateClick() : navigate("create")
                                    }}
                            />
                    </Fragment>
                    </div>
                    <div className="level-right">
                        {props.filterColumns && props.filterColumns.length > 0 &&
                            <TableFilter
                                title={t('title.filter')}
                                columns={props.filterColumns}
                                filter={useTableHandle.filter}
                                tableHandle={useTableHandle}
                                onFilter={useTableHandle.onFilter}
                            />
                        }
                    </div>
                </div>
            }
        </div>
    )
}

DataList.propTypes = {
    selector: PropTypes.func.isRequired,
    loadingSelector: PropTypes.func,
    fetchAction: PropTypes.func.isRequired,
    columns: PropTypes.array.isRequired,
    onClick: PropTypes.func,
    disableOnClick: PropTypes.bool,
    createButtonLabel: PropTypes.string,
    showCreateButton: PropTypes.bool,
    onCreateClick: PropTypes.func,
    defaultSort: PropTypes.string,
    defaultPageable: PropTypes.string,
    groupRowsBy: PropTypes.string,
    filterColumns: PropTypes.arrayOf(PropTypes.object),
    defaultFilter: PropTypes.string,
    refreshState: PropTypes.string,
    cardTitle: PropTypes.string,
}

export default DataList;
